import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { campusPath, get_public_branches } from "../../../apis/Branches";
import { C, FONT, sectionTitle, sectionLead } from "../../../constants/mq";

// Fallback for when the API is unreachable — keeps the page from looking
// broken on first deploy or temporary outages.
const fallbackHigh = [
  { id: "fb-1", name: "Norwest", region: "The Hills District", state: "NSW", address: "Unit 121, 14 Lexington Dr, Bella Vista NSW 2153" },
  { id: "fb-2", name: "Castle Hill", region: "The Hills District", state: "NSW", address: "Castle Hill NSW" },
  { id: "fb-3", name: "Epping", region: "Northern Sydney", state: "NSW", address: "Epping NSW" },
  { id: "fb-4", name: "Blacktown", region: "Western Sydney", state: "NSW", address: "Blacktown NSW" },
];

// Year 11-12, Year 7-10 and primary locations as sand panel lists, like the
// "Important dates" panel on mq.edu.au.
const styles = {
  section: { background: "#ffffff", padding: "84px 0 88px", fontFamily: FONT },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  header: { textAlign: "center", maxWidth: 720, margin: "0 auto 40px" },
  heading: { ...sectionTitle },
  subheading: { ...sectionLead },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: 24,
  },
  card: { padding: "26px 24px 28px", display: "flex", flexDirection: "column" },
  label: {
    fontSize: 11, fontWeight: 700, letterSpacing: "1.4px", textTransform: "uppercase",
    color: C.red, margin: "0 0 12px",
  },
  campusName: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px" },
  campusState: { fontSize: 13, color: C.muted, fontWeight: 600, margin: "0 0 12px" },
  campusAddress: { fontSize: 13.5, lineHeight: 1.55, color: C.body, margin: "0 0 18px", flex: 1 },
  primaryWrap: { marginTop: 48, padding: "26px 28px 18px" },
  primaryHeading: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px" },
  primaryNote: { fontSize: 13.5, color: C.body, margin: "0 0 10px" },
  primaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    columnGap: 40,
  },
  primaryRow: {
    display: "flex", alignItems: "baseline", gap: 14,
    padding: "13px 0", borderBottom: `1px solid ${C.line}`, textDecoration: "none",
  },
  primaryCardName: { fontSize: 14, fontWeight: 700, color: C.ink, margin: 0, minWidth: 120 },
  primaryCardMeta: { fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.4 },
};

// One sand panel of campus rows: name, then region and phone.
const LocationList = ({ title, note, branches, gap = false }) => (
  <div className="mq-sand" style={{ ...styles.primaryWrap, marginTop: gap ? 24 : 0 }}>
    <h3 style={styles.primaryHeading}>{title}</h3>
    <p style={styles.primaryNote}>{note}</p>
    <div style={styles.primaryGrid}>
      {branches.map((b) => {
        const metaParts = [b.region || b.state || "", b.phone].filter(Boolean);
        return (
          <Link key={b.id} to={campusPath(b)} style={styles.primaryRow} className="cs-primary-row">
            <p style={styles.primaryCardName}>{b.name}</p>
            {metaParts.length > 0 && <p style={styles.primaryCardMeta}>{metaParts.join(" · ")}</p>}
          </Link>
        );
      })}
    </div>
  </div>
);

function Campuses() {
  const [seniorBranches, setSeniorBranches] = useState([]);
  const [highBranches, setHighBranches] = useState(fallbackHigh);
  const [primaryBranches, setPrimaryBranches] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [senior, high, primary] = await Promise.all([
        get_public_branches("senior"),
        get_public_branches("high"),
        get_public_branches("primary"),
      ]);
      if (cancelled) return;
      if (Array.isArray(senior)) setSeniorBranches(senior);
      if (Array.isArray(high) && high.length > 0) setHighBranches(high);
      if (Array.isArray(primary)) setPrimaryBranches(primary);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.heading}>Tutoring locations in Sydney</h2>
          <p style={styles.subheading}>
            Year 11–12 and Year 7–10 programs run from the campuses below.
            Primary courses are available across many more locations, listed
            further down.
          </p>
        </div>

        {/* All three levels in the same compact list: there are too many
            campuses for a card each. A campus can be in more than one. */}
        {seniorBranches.length > 0 && (
          <LocationList
            title="High School (Year 11–12)"
            note="Senior courses run at these campuses."
            branches={seniorBranches}
          />
        )}
        <LocationList
          title="High School (Year 7–10)"
          note="Junior high school courses run at these campuses."
          branches={highBranches}
          gap={seniorBranches.length > 0}
        />
        {primaryBranches.length > 0 && (
          <LocationList
            title="Primary"
            note="We run primary courses across more locations."
            branches={primaryBranches}
            gap
          />
        )}
        <div style={{ marginTop: 18, textAlign: "center" }}>
          <Link className="mq-link" to="/our-branches">See all campuses</Link>
        </div>
      </div>

      <style>{`
        .cs-primary-row:hover p:first-child { color: ${C.red} !important; }
      `}</style>
    </section>
  );
}

export default Campuses;
